import { get, difference } from 'lodash';

import { ASSET_TYPE, LANDING_ASSET_TYPE } from 'src/data/asset';
import { contentTypeFromBriefType } from 'src/data/brief';
import { CONTENT_TYPE } from 'src/data/contentType';
import { isContinuousOptimizationType } from './projectType';
import { orderCOAssets } from '../continuousOptimization';
import { PROJECT_TYPE } from './const';

export const PROJECT_ASSET_TYPES_ASSOCIATION = {
  [PROJECT_TYPE.canvas]: [
    ASSET_TYPE.canvasAida,
    ASSET_TYPE.canvasPas,
    ASSET_TYPE.canvasBab,
    ASSET_TYPE.canvasFeatureToBenefitsPractical,
    ASSET_TYPE.canvasPerfectHeadline,
    ASSET_TYPE.canvasPersuasiveBulletPoint,
    ASSET_TYPE.canvasValueProposition,
    ASSET_TYPE.canvasProductDescriptionList,
    ASSET_TYPE.canvasProductDescriptionRelaxed,
    ASSET_TYPE.canvasBlogIdeas,
    ASSET_TYPE.canvasVideoIdeas,
    ASSET_TYPE.canvasContentImprover,
    ASSET_TYPE.canvasEli5,
    ASSET_TYPE.canvasSentenceExpander,
  ],
  [PROJECT_TYPE.facebookAd]: [ASSET_TYPE.facebookPrimaryText, ASSET_TYPE.facebookHeadline],
  [PROJECT_TYPE.googleAd]: [ASSET_TYPE.googleDescription, ASSET_TYPE.googleHeadline],
  [PROJECT_TYPE.taboolaAd]: [ASSET_TYPE.taboolaHeadline],
  [PROJECT_TYPE.outbrainAd]: [ASSET_TYPE.outbrainHeadline],
  [PROJECT_TYPE.linkedinAd]: [], // Based on the Brief Type
  [PROJECT_TYPE.twitterAd]: [], // Based on the Brief Type
  [PROJECT_TYPE.pinterestAd]: [ASSET_TYPE.pinterestDescription, ASSET_TYPE.pinterestTitle],
  [PROJECT_TYPE.teadsSingleImage]: [
    ASSET_TYPE.teadsSingleImageHeadline,
    ASSET_TYPE.teadsSingleImageDescription,
  ],
  [PROJECT_TYPE.sms]: [ASSET_TYPE.sms],
  [PROJECT_TYPE.emailSubjectLine]: [ASSET_TYPE.emailSubjectLine],
  [PROJECT_TYPE.emailPromotional]: [
    ASSET_TYPE.emailPromotionalSubjectLine,
    ASSET_TYPE.emailPromotionalBody,
  ],
  [PROJECT_TYPE.emailColdOutreach]: [
    ASSET_TYPE.emailColdOutreachSubjectLine,
    ASSET_TYPE.emailColdOutreachBody,
  ],
  [PROJECT_TYPE.teadsSingleVideo]: [
    ASSET_TYPE.teadsSingleVideoHeadline,
    ASSET_TYPE.teadsSingleVideoDescription,
  ],
  [PROJECT_TYPE.teadsCarousel]: [ASSET_TYPE.teadsCarouselHeadline],
  [PROJECT_TYPE.teadsSocialLayout1]: [
    ASSET_TYPE.teadsSocialLayout1Headline,
    ASSET_TYPE.teadsSocialLayout1PrimaryText,
    ASSET_TYPE.teadsSocialLayout1Description,
  ],
  [PROJECT_TYPE.teadsSocialLayout2]: [ASSET_TYPE.teadsSocialLayout2PrimaryText],

  [PROJECT_TYPE.facebookSocial]: [ASSET_TYPE.facebookPost],
  [PROJECT_TYPE.instagramSocial]: [ASSET_TYPE.instagramCaption],
  [PROJECT_TYPE.twitterSocial]: [ASSET_TYPE.tweet],
  [PROJECT_TYPE.linkedinSocial]: [ASSET_TYPE.linkedinPost],
  [PROJECT_TYPE.pinterestSocial]: [ASSET_TYPE.pinterestPost, ASSET_TYPE.pinterestPostTitle],
  [PROJECT_TYPE.youtubeSocial]: [ASSET_TYPE.youtubeTitle, ASSET_TYPE.youtubeDescription],

  [PROJECT_TYPE.productDescription]: [ASSET_TYPE.landingProductDescription],
  [PROJECT_TYPE.amazonProductDescription]: [
    ASSET_TYPE.amazonProductDescription,
    ASSET_TYPE.amazonFeatureList,
  ],
  [PROJECT_TYPE.walmartProductListing]: [
    ASSET_TYPE.walmartProductDescription,
    ASSET_TYPE.walmartFeatureList,
  ],
  [PROJECT_TYPE.shopifyProduct]: [
    ASSET_TYPE.landingProductName,
    ASSET_TYPE.landingProductDescription,
  ],
  [PROJECT_TYPE.shopifyProductsDescription]: [ASSET_TYPE.landingProductDescription],
  [PROJECT_TYPE.seoMetaDescription]: [ASSET_TYPE.landingSEOMetadata],
  [PROJECT_TYPE.featuresToBenefits]: [ASSET_TYPE.consumerBenefits],
  [PROJECT_TYPE.landingPageBuilder]: [
    LANDING_ASSET_TYPE.landingBuilderHeadline,
    LANDING_ASSET_TYPE.landingBuilderSubHeadline,
    LANDING_ASSET_TYPE.landingBuilderCta,
    LANDING_ASSET_TYPE.benefits,
    LANDING_ASSET_TYPE.features,
  ],

  [PROJECT_TYPE.blogPost]: [
    ASSET_TYPE.blogPostOutline,
    ASSET_TYPE.blogPostTitle,
    ASSET_TYPE.blogPostIntroParagraph,
  ],
  [PROJECT_TYPE.continuousOptimization]: [
    ASSET_TYPE.landingCopy,
    ASSET_TYPE.landingProductName,
    ASSET_TYPE.landingCTA,
    ASSET_TYPE.landingHeadline,
  ],
  [PROJECT_TYPE.wtmHomePage]: [
    ASSET_TYPE.landingCopy,
    ASSET_TYPE.landingProductName,
    ASSET_TYPE.landingCTA,
    ASSET_TYPE.landingHeadline,
  ],
};

const LINKEDIN_ASSET_TYPES_ASSOCIATION = {
  [CONTENT_TYPE.content]: [ASSET_TYPE.linkedinIntro, ASSET_TYPE.linkedinTitle],
  [CONTENT_TYPE.product]: [ASSET_TYPE.linkedinTextAdDescription, ASSET_TYPE.linkedinTextAdHeadline],
};

const TWITTER_ASSET_TYPES_ASSOCIATION = {
  [CONTENT_TYPE.content]: [ASSET_TYPE.twitterCopy],
  [CONTENT_TYPE.product]: [ASSET_TYPE.twitterCopy, ASSET_TYPE.twitterTitle],
};

const addMissingGeneratedTypes = (assetTypes, generatedAssetTypes) => {
  const missingTypes = difference(generatedAssetTypes, assetTypes);
  if (missingTypes.length > 0) {
    return [...assetTypes, ...missingTypes];
  }
  return assetTypes;
};

export const getAssetsTypesAssociatedWithProject = (project) => {
  const assets = PROJECT_ASSET_TYPES_ASSOCIATION[project.type];

  if (assets && assets.length) {
    return addMissingGeneratedTypes(assets, project.generatedAssetTypes);
  }

  switch (project.type) {
    case PROJECT_TYPE.linkedinAd:
      return LINKEDIN_ASSET_TYPES_ASSOCIATION[contentTypeFromBriefType(project.brief.type)] || [];
    case PROJECT_TYPE.twitterAd:
      return TWITTER_ASSET_TYPES_ASSOCIATION[contentTypeFromBriefType(project.brief.type)] || [];
    default:
      return [];
  }
};

export const doesProjectTypeSupportAssetType = (project, assetType) => {
  const supportedAssetTypes = getAssetsTypesAssociatedWithProject(project);
  return supportedAssetTypes.includes(assetType);
};

export const getInitialAssetType = (project) => {
  // Check if we can pre-select "last used asset type"
  const lastUsedAssetType = get(project, 'generationRequests.edges[0].node.assetType');
  if (lastUsedAssetType && doesProjectTypeSupportAssetType(project, lastUsedAssetType)) {
    return lastUsedAssetType;
  }

  const landingPageAssets = isContinuousOptimizationType(project.type)
    ? get(project, 'landingPageAssets')
    : null;

  if (landingPageAssets && landingPageAssets.length > 0) {
    const assets = orderCOAssets(landingPageAssets);
    return assets[0].type;
  }
  const assets = getAssetsTypesAssociatedWithProject(project);

  return (
    // Select first assetType
    assets[0] ||
    // Something strange
    null
  );
};
